import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { contactData } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit to backend API
      const response = await axios.post(`${BACKEND_URL}/api/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        project_type: formData.projectType,
        message: formData.message
      });

      if (response.status === 200) {
        toast.success('Message sent successfully! We will contact you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-sm tracking-[0.3em] uppercase text-gray-500 font-light">
              Get In Touch
            </span>
            <div className="h-px w-16 bg-gray-900 mt-2 mx-auto" />
          </div>
          <h2 className="text-4xl lg:text-5xl font-light text-gray-900 mb-6">
            Start Your Project
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Let's discuss how we can bring your architectural vision to life with precision-engineered glazing solutions
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div className="space-y-8">
            {/* Contact Cards */}
            <div className="space-y-6">
              {/* Phone */}
              <div className="group flex items-start gap-4 p-6 bg-white rounded-lg border border-gray-200 hover:border-gray-900 hover:shadow-lg transition-all duration-500">
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-900 transition-colors duration-500">
                  <Phone className="w-6 h-6 text-gray-700 group-hover:text-white transition-colors duration-500" />
                </div>
                <div>
                  <h3 className="text-sm tracking-wider uppercase text-gray-500 mb-2">Phone</h3>
                  <a href={`tel:${contactData.phone[0]}`} className="text-lg text-gray-900 hover:text-gray-600 transition-colors block mb-1">
                    {contactData.phone[0]}
                  </a>
                  <a href={`tel:${contactData.phone[1]}`} className="text-lg text-gray-900 hover:text-gray-600 transition-colors block">
                    {contactData.phone[1]}
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="group flex items-start gap-4 p-6 bg-white rounded-lg border border-gray-200 hover:border-gray-900 hover:shadow-lg transition-all duration-500">
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-900 transition-colors duration-500">
                  <Mail className="w-6 h-6 text-gray-700 group-hover:text-white transition-colors duration-500" />
                </div>
                <div>
                  <h3 className="text-sm tracking-wider uppercase text-gray-500 mb-2">Email</h3>
                  <a href={`mailto:${contactData.email}`} className="text-lg text-gray-900 hover:text-gray-600 transition-colors break-all">
                    {contactData.email}
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="group flex items-start gap-4 p-6 bg-white rounded-lg border border-gray-200 hover:border-gray-900 hover:shadow-lg transition-all duration-500">
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-900 transition-colors duration-500">
                  <MapPin className="w-6 h-6 text-gray-700 group-hover:text-white transition-colors duration-500" />
                </div>
                <div>
                  <h3 className="text-sm tracking-wider uppercase text-gray-500 mb-2">Address</h3>
                  <p className="text-base text-gray-900 leading-relaxed">
                    {contactData.address}
                  </p>
                  <p className="text-base text-gray-600 mt-2">
                    <span className="font-medium">Coverage:</span> {contactData.coverage}
                  </p>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-gray-900 text-white p-8 rounded-lg">
              <h3 className="text-lg tracking-wider uppercase mb-4">Business Hours</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-300">Monday - Saturday</span>
                  <span className="font-medium">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Sunday</span>
                  <span className="font-medium">Closed</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-lg">
            <h3 className="text-2xl font-light text-gray-900 mb-6">
              Book a Site Consultation
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm tracking-wider uppercase text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-300"
                  placeholder="Enter your name"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm tracking-wider uppercase text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-300"
                  placeholder="Enter your email"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm tracking-wider uppercase text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-300"
                  placeholder="Enter your phone number"
                />
              </div>

              {/* Project Type */}
              <div>
                <label htmlFor="projectType" className="block text-sm tracking-wider uppercase text-gray-700 mb-2">
                  Project Type *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-300"
                >
                  <option value="">Select project type</option>
                  <option value="Aluminium Windows">Aluminium Windows & Doors</option>
                  <option value="Structural Glazing">Structural Glazing</option>
                  <option value="Curtain Wall">Curtain Wall Systems</option>
                  <option value="ACP Cladding">ACP Cladding</option>
                  <option value="Skylights">Skylights & Canopies</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm tracking-wider uppercase text-gray-700 mb-2">
                  Project Details *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-300 resize-none"
                  placeholder="Tell us about your project requirements"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gray-900 text-white py-4 rounded-lg hover:bg-gray-800 transition-all duration-300 flex items-center justify-center gap-2 font-medium tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};